import axios from 'axios'

import { COP_COMMON_ERROR_MESSAGES } from '../constants/common-error-messages'
import { COP_API_BASE_URL, getAgreementListBySiteUrl } from '../constants/service-endpoints'
import { AgreementListResponse } from './agreement'
import { ToastService } from './toast.service'
import { buildCopHeaders, getSilentToken } from './tokenUtils'

const API_PATH = '/api/v2/odata/sites/GetListForEncompassAgreement'
const SELECT_FIELDS = 'city,country,customerSiteName,id,latitude,longitude,name,operator,owner,operationalStatus'

export interface ODataListParams {
  orderby?: string
  count?: boolean
  top?: number
  skip?: number
  filter?: string
}

export interface AgreementListParams {
  top?: number
  skip?: number
  orderby?: string
  count?: boolean
  filter?: string
}

export interface SiteListParams {
  encompassAgreementId: string
  top?: number
  skip?: number
  orderby?: string
  count?: boolean
  filter?: string
}

export interface Site {
  id: string
  name: string
  customerSiteName: string
  city: string
  country: string
  operationalStatus: string
  operator: string
  owner: string
  latitude: string
  longitude: string
}

export interface SiteListResponse {
  '@odata.count'?: number
  value: Site[]
}

function buildFilterExpression(search: string): string {
  const escaped = search.replace(/'/g, "''")
  return `((contains(name,'${escaped}')) or (contains(city,'${escaped}')) or (contains(country,'${escaped}')))`
}

function buildODataQuery(params: Required<SiteListParams>): string {
  const query = new URLSearchParams()
  query.set('$select', SELECT_FIELDS)
  query.set('$orderby', params.orderby)
  query.set('$count', String(params.count))
  query.set('$top', String(params.top))
  query.set('$skip', String(params.skip))
  if (params.filter) {
    query.set('$filter', buildFilterExpression(params.filter))
  }
  return `?${query.toString()}`
}

export async function fetchSites(
  params: SiteListParams,
): Promise<SiteListResponse | null> {
  const {
    encompassAgreementId,
    top = 10,
    skip = 0,
    orderby = 'name asc',
    count = true,
    filter = '',
  } = params

  try {
    const token = getSilentToken();
    const escapedId = encompassAgreementId.replace(/'/g, "''")
    const url = `${COP_API_BASE_URL}${API_PATH}(encompassAgreementId='${escapedId}')${buildODataQuery({ encompassAgreementId, top, skip, orderby, count, filter })}`
    const headers = buildCopHeaders(token)
    const response = await axios.get<SiteListResponse>(url, { headers })
    const { '@odata.count': odataCount, value } = response.data
    return { '@odata.count': odataCount, value }
  } catch (error) {
    const fallbackMessage = COP_COMMON_ERROR_MESSAGES['api-error'].message.replace(
      '{{action}}',
      COP_COMMON_ERROR_MESSAGES['api-error'].action['sites-loading'],
    )
    const message = error instanceof Error && error.message ? error.message : fallbackMessage
    ToastService.error(message)
    return null
  }
}

function buildAgreementFilterExpression(search: string): string {
  const escaped = search.replace(/'/g, "''")
  return `((contains(legalEntityName,'${escaped}')) or (contains(number,'${escaped}')))`
}

export async function fetchAgreementsBySite(
  siteId: string,
  params: AgreementListParams = {},
): Promise<AgreementListResponse | null> {
  const {
    top = 10,
    skip = 0,
    orderby = 'validTo asc',
    count = true,
    filter = '',
  } = params

  try {
    const token = getSilentToken();
    const filterParam = filter ? buildAgreementFilterExpression(filter) : '';
    const url = `${COP_API_BASE_URL}${getAgreementListBySiteUrl(siteId, { orderby, count, top, skip, filter: filterParam })}`
    const headers = buildCopHeaders(token)
    const response = await axios.get<AgreementListResponse>(url, { headers })
    const { '@odata.count': odataCount, value } = response.data
    return { '@odata.count': odataCount, value }
  } catch (error) {
    const fallbackMessage = COP_COMMON_ERROR_MESSAGES['api-error'].message.replace(
      '{{action}}',
      COP_COMMON_ERROR_MESSAGES['api-error'].action['agreements-by-site-loading'],
    )
    const message = error instanceof Error && error.message ? error.message : fallbackMessage
    ToastService.error(message)
    return null
  }
}

